import { createContext, useContext, useEffect, useState } from "react";

type Theme = "dark" | "light" | "system";

interface ThemeContextValue {
  theme: Theme;
  isDark: boolean;
  setTheme: (theme: Theme) => void;
}

const VALID_THEMES: readonly Theme[] = ["dark", "light", "system"];

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (process.env.NODE_ENV !== "production" && ctx === undefined) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return ctx!; // production: context contract is upheld structurally
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    const stored = localStorage.getItem("theme");
    return VALID_THEMES.includes(stored as Theme)
      ? (stored as Theme)
      : "system";
  });

  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem("theme");
    const t = VALID_THEMES.includes(stored as Theme)
      ? (stored as Theme)
      : "system";
    if (t === "dark") return true;
    if (t === "light") return false;
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  function setTheme(next: Theme) {
    setThemeState(next);
    localStorage.setItem("theme", next);
  }

  useEffect(() => {
    const root = document.documentElement;
    if (theme !== "system") {
      const dark = theme === "dark";
      root.classList.toggle("dark", dark);
      setIsDark(dark);
      return;
    }
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      root.classList.toggle("dark", mq.matches);
      setIsDark(mq.matches);
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, isDark, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}
